import type {
  AiOpenMode,
  AssistantConnection,
  AssistantPresentation,
  AssistantVisibility,
} from '../protocol'

export interface CanonicalPresentationState {
  visibility: AssistantVisibility
  preferredPresentation: AssistantPresentation
}

/** Convert one legacy open mode into the canonical orthogonal state. */
export function canonicalPresentationFromLegacy(mode: AiOpenMode): CanonicalPresentationState {
  switch (mode) {
    case 'launcher':
      return { visibility: 'closed', preferredPresentation: 'compact' }
    case 'drawer':
      return { visibility: 'open', preferredPresentation: 'docked' }
    case 'fullscreen':
      return { visibility: 'open', preferredPresentation: 'workspace' }
    case 'floating':
    case 'focus-modal':
    default:
      return { visibility: 'open', preferredPresentation: 'compact' }
  }
}

/** Return the stable legacy mode used by deprecated public callbacks. */
export function legacyModeFromCanonical(
  visibility: AssistantVisibility,
  presentation: AssistantPresentation
): AiOpenMode {
  if (visibility === 'closed') return 'launcher'
  if (presentation === 'docked') return 'drawer'
  if (presentation === 'workspace') return 'fullscreen'
  return 'floating'
}

/**
 * Resolve layout for the current viewport without mutating user preference.
 * Below 900px every preferred layout becomes the full-viewport workspace.
 */
export function resolveAssistantPresentation(
  preferred: AssistantPresentation,
  viewportWidth: number
): AssistantPresentation {
  return viewportWidth < 900 ? 'workspace' : preferred
}

/** Map transport-specific status strings to the public connection vocabulary. */
export function assistantConnectionFromStreamStatus(status: string): AssistantConnection {
  switch (status) {
    case 'connecting':
      return 'connecting'
    case 'streaming':
      return 'online'
    case 'reconnecting':
      return 'reconnecting'
    case 'error':
      return 'offline'
    default:
      return 'idle'
  }
}
